import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
    getRoles, getJobPositions, getDepartments, getEmploymentStatuses,
    addLookupItem, updateLookupItem, deleteLookupItem,
} from "../../services/LookupService";
import { getAppSettings, updateAppSetting } from "../../services/AppSettingService";
import { API_ENDPOINTS } from "../../services/Constants";

const PageWrapper = styled.div`
    max-width: 860px;
    margin: 2rem auto;
    padding: 0 1rem;
`;

const PageTitle = styled.h2`
    color: ${({ theme }) => theme.colors.text};
    margin-bottom: 1.5rem;
    font-size: 1.6rem;
`;

const TabBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    border-bottom: 2px solid ${({ theme }) => theme.colors.lineColor};
    margin-bottom: 1.5rem;
`;

const Tab = styled.button`
    padding: 0.55rem 1.1rem;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.text};
    box-shadow: ${({ $active, theme }) => $active ? `inset 0 -2px 0 ${theme.colors.primary}` : "none"};
    white-space: nowrap;
    transition: color 0.15s, box-shadow 0.15s;
    &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

const Card = styled.div`
    background: ${({ theme }) => theme.colors.card};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    box-shadow: ${({ theme }) => theme.shadows.md};
    padding: 1.5rem;
`;

const ItemList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 1rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
`;

const ItemRow = styled.li`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.6rem;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    border: 1px solid ${({ theme }) => theme.colors.lineColor};
`;

const ItemName = styled.span`
    flex: 1;
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.9rem;
`;

const EditInput = styled.input`
    flex: 1;
    padding: 0.3rem 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.primary};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.9rem;
    font-family: ${({ theme }) => theme.typography.fontFamily};
`;

const SmallBtn = styled.button`
    padding: 0.25rem 0.6rem;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    background: ${({ variant, theme }) =>
        variant === "danger" ? theme.status.error :
        variant === "primary" ? theme.colors.primary :
        theme.colors.buttonBackground};
    color: ${({ variant, theme }) =>
        variant ? "#fff" : theme.colors.buttonText};
    &:hover:not(:disabled) { opacity: 0.85; }
    &:disabled { opacity: 0.5; cursor: default; }
`;

const AddRow = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const AddInput = styled.input`
    flex: 1;
    padding: 0.4rem 0.7rem;
    border: 1px solid ${({ theme }) => theme.colors.lineColor};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.9rem;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const ErrorMsg = styled.p`
    color: ${({ theme }) => theme.status.error};
    font-size: 0.82rem;
    margin: 0.3rem 0 0 0;
`;

const RoleTag = styled.span`
    display: inline-block;
    padding: 0.2rem 0.6rem;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: ${({ theme }) => theme.colors.primary}22;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.8rem;
    font-weight: 600;
    margin-right: 0.5rem;
`;

// ── Tab sadržaji ──────────────────────────────────────────────────

function AppSettingsTab() {
    const [settings, setSettings] = useState([]);
    const [editKey, setEditKey] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getAppSettings().then(setSettings).catch(() => setError("Greška pri učitavanju postavki"));
    }, []);

    const handleEdit = (s) => {
        setEditKey(s.key);
        setEditValue(s.value);
        setError("");
    };

    const handleSave = async () => {
        const val = editValue.trim();
        if (!val || isNaN(Number(val)) || !Number.isInteger(Number(val)) || Number(val) < 1) {
            setError("Vrijednost mora biti pozitivan cijeli broj.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const updated = await updateAppSetting(editKey, val);
            setSettings((prev) => prev.map((s) => s.key === editKey ? updated : s));
            setEditKey(null);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <ItemList>
                {settings.map((s) => (
                    <ItemRow key={s.key}>
                        {editKey === s.key ? (
                            <>
                                <ItemName style={{ fontWeight: 600 }}>{s.description}</ItemName>
                                <EditInput
                                    type="number"
                                    min="1"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleSave()}
                                    autoFocus
                                    style={{ maxWidth: "80px" }}
                                />
                                <SmallBtn variant="primary" onClick={handleSave} disabled={loading}>Spremi</SmallBtn>
                                <SmallBtn onClick={() => setEditKey(null)} disabled={loading}>Odustani</SmallBtn>
                            </>
                        ) : (
                            <>
                                <ItemName>{s.description}</ItemName>
                                <RoleTag>{s.value}</RoleTag>
                                <SmallBtn onClick={() => handleEdit(s)} disabled={loading}>Uredi</SmallBtn>
                            </>
                        )}
                    </ItemRow>
                ))}
            </ItemList>
            {error && <ErrorMsg>{error}</ErrorMsg>}
        </Card>
    );
}

function RolesTab({ roles }) {
    return (
        <Card>
            <ItemList>
                {roles.map((r) => (
                    <ItemRow key={r.id}>
                        <RoleTag>{r.name}</RoleTag>
                        <ItemName>{r.displayName}</ItemName>
                    </ItemRow>
                ))}
            </ItemList>
        </Card>
    );
}

function LookupTab({ items, endpoint, onRefresh }) {
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");
    const [newName, setNewName] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleEdit = (item) => { setEditId(item.id); setEditName(item.name); setError(""); };

    const handleSaveEdit = async () => {
        if (!editName.trim()) return;
        setLoading(true); setError("");
        try {
            await updateLookupItem(endpoint, editId, editName.trim());
            setEditId(null);
            onRefresh();
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    };

    const handleDelete = async (id) => {
        setLoading(true); setError("");
        try { await deleteLookupItem(endpoint, id); onRefresh(); }
        catch (e) { setError(e.message); }
        finally { setLoading(false); }
    };

    const handleAdd = async () => {
        if (!newName.trim()) return;
        setLoading(true); setError("");
        try {
            await addLookupItem(endpoint, newName.trim());
            setNewName("");
            onRefresh();
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    };

    return (
        <Card>
            <ItemList>
                {items.map((item) => (
                    <ItemRow key={item.id}>
                        {editId === item.id ? (
                            <>
                                <EditInput
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleSaveEdit()}
                                    autoFocus
                                />
                                <SmallBtn variant="primary" onClick={handleSaveEdit} disabled={loading}>Spremi</SmallBtn>
                                <SmallBtn onClick={() => setEditId(null)} disabled={loading}>Odustani</SmallBtn>
                            </>
                        ) : (
                            <>
                                <ItemName>{item.name}</ItemName>
                                <SmallBtn onClick={() => handleEdit(item)} disabled={loading}>Uredi</SmallBtn>
                                <SmallBtn variant="danger" onClick={() => handleDelete(item.id)} disabled={loading}>Obriši</SmallBtn>
                            </>
                        )}
                    </ItemRow>
                ))}
            </ItemList>
            {error && <ErrorMsg>{error}</ErrorMsg>}
            <AddRow>
                <AddInput
                    placeholder="Novi unos..."
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                />
                <SmallBtn variant="primary" onClick={handleAdd} disabled={loading || !newName.trim()}>
                    + Dodaj
                </SmallBtn>
            </AddRow>
        </Card>
    );
}

// ── Glavna komponenta ─────────────────────────────────────────────

const TABS = [
    { id: "settings",   label: "Postavke aplikacije" },
    { id: "roles",      label: "Uloge" },
    { id: "positions",  label: "Radne pozicije" },
    { id: "depts",      label: "Odjeli" },
    { id: "statuses",   label: "Statusi zaposlenja" },
];

function LookupManagement() {
    const [activeTab, setActiveTab] = useState("settings");
    const [roles, setRoles] = useState([]);
    const [jobPositions, setJobPositions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [employmentStatuses, setEmploymentStatuses] = useState([]);

    const load = useCallback(async () => {
        try {
            const [r, jp, d, es] = await Promise.all([
                getRoles(), getJobPositions(), getDepartments(), getEmploymentStatuses()
            ]);
            setRoles(r);
            setJobPositions(jp);
            setDepartments(d);
            setEmploymentStatuses(es);
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <PageWrapper>
            <PageTitle>Postavke</PageTitle>

            <TabBar>
                {TABS.map((t) => (
                    <Tab key={t.id} $active={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
                        {t.label}
                    </Tab>
                ))}
            </TabBar>

            {activeTab === "settings"  && <AppSettingsTab />}
            {activeTab === "roles"     && <RolesTab roles={roles} />}
            {activeTab === "positions" && <LookupTab items={jobPositions} endpoint={API_ENDPOINTS.LOOKUP_JOB_POSITIONS} onRefresh={load} />}
            {activeTab === "depts"     && <LookupTab items={departments} endpoint={API_ENDPOINTS.LOOKUP_DEPARTMENTS} onRefresh={load} />}
            {activeTab === "statuses"  && <LookupTab items={employmentStatuses} endpoint={API_ENDPOINTS.LOOKUP_EMPLOYMENT_STATUSES} onRefresh={load} />}
        </PageWrapper>
    );
}

export default LookupManagement;
